import { useAuth0 } from '@auth0/auth0-react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import { ClientVoiceQuote, User } from '../types';
import ClientVoiceEditor from './ClientVoiceEditor';

interface ClientVoiceSectionProps {
  clientVoice: ClientVoiceQuote[];
  clientId: string;
  onUpdate: (updatedVoices: ClientVoiceQuote[]) => void;
}

const ClientVoiceSection: React.FC<ClientVoiceSectionProps> = ({ clientVoice, clientId, onUpdate }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  const itemsPerPage = 3;

  // Calculate pagination values
  const totalPages = Math.ceil(clientVoice.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = clientVoice.slice(startIndex, endIndex);

  // Navigation functions
  const goToPreviousPage = () => {
    setCurrentPage(prev => prev > 0 ? prev - 1 : totalPages - 1);
  };

  const goToNextPage = () => {
    setCurrentPage(prev => prev < totalPages - 1 ? prev + 1 : 0);
  };

  // Fetch user permissions
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = await getAccessTokenSilently();
        apiService.setAuthToken(token);
        const userData = await apiService.getCurrentUser();
        setUser(userData);
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };

    fetchUser();
  }, [getAccessTokenSilently]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleEditorSave = (updatedVoices: ClientVoiceQuote[]) => {
    onUpdate(updatedVoices);
    setIsEditing(false);
    // Reset to first page if current page would be empty
    const newTotalPages = Math.ceil(updatedVoices.length / itemsPerPage);
    if (currentPage >= newTotalPages && newTotalPages > 0) {
      setCurrentPage(0);
    }
  };

  const handleEditorClose = () => {
    setIsEditing(false);
  };

  // Don't show pagination if there are no items
  if (clientVoice.length === 0) {
    return (
      <>
        <section className="mb-16" id="client-voice">
          <div className="flex items-center gap-3 mb-6">
            <RecordVoiceOverOutlinedIcon className="text-gray-700" style={{ fontSize: 28 }} />
            <h2 className="text-[32px] font-bold text-gray-900 m-0">
              Client Voice
            </h2>
            {user?.canEdit ? <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick();
                }}
                style={{
                  background: 'transparent',
                  color: '#3b82f6',
                  border: '1px solid #bfdbfe',
                  borderRadius: 8,
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#eff6ff';
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#bfdbfe';
                }}
              >
                <EditIcon style={{ fontSize: 14 }} />
                Edit
              </button>
            </div> : null}
          </div>
          <div className="mb-6">
            <div className="flex justify-between items-center mb-8">
              <div className="bg-[rgba(250,251,253,1)] px-4 py-2 rounded-full text-gray-600 text-[18px]">
                Direct quotes from the client — questions, concerns, and recurring comments.
              </div>
            </div>
            <div className="text-[18px] text-gray-500 text-center py-8">
              No client voice data available.
            </div>
          </div>
        </section>
        <ClientVoiceEditor
          isOpen={isEditing}
          onClose={handleEditorClose}
          clientId={clientId}
          initialVoices={clientVoice}
          onSave={handleEditorSave}
        />
      </>
    );
  }

  return (
    <>
      <section className="mb-16" id="client-voice">
        <div className="flex items-center gap-3 mb-6">
          <RecordVoiceOverOutlinedIcon className="text-gray-700" style={{ fontSize: 28 }} />
          <h2 className="text-[32px] font-bold text-gray-900 m-0">
            Client Voice
          </h2>
          {user?.canEdit ? (
            <button
              onClick={handleEditClick}
              className="ml-2 p-1 ml-auto text-gray-500 hover:text-gray-700 transition-colors duration-200"
              title="Edit client voice"
            >
              <EditIcon style={{ fontSize: 20 }} />
            </button>
          ) : null}
        </div>
        <div className="mb-6">
          <div className="flex justify-between items-center mb-8">
            <div className="bg-[rgba(250,251,253,1)] px-4 py-2 rounded-full text-gray-600 text-[18px]">
              Direct quotes from the client — questions, concerns, and recurring comments.
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-3">
                <span className="text-gray-500 text-sm">
                  {currentPage + 1} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={goToPreviousPage}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200 disabled:opacity-30"
                    disabled={totalPages <= 1}
                  >
                    <ChevronLeftIcon style={{ fontSize: 20 }} />
                  </button>
                  <button
                    onClick={goToNextPage}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200 disabled:opacity-30"
                    disabled={totalPages <= 1}
                  >
                    <ChevronRightIcon style={{ fontSize: 20 }} />
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-8">
            {currentItems.map((quote) => (
              <div key={quote.id} className="border-l-2 border-gray-300 pl-6">
                <div className="text-gray-900 text-[18px] mb-2">
                  "{quote.quote}"
                </div>
                <div className="text-gray-900 font-bold text-[16px]">
                  → {quote.source}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <ClientVoiceEditor
        isOpen={isEditing}
        onClose={handleEditorClose}
        clientId={clientId}
        initialVoices={clientVoice}
        onSave={handleEditorSave}
      />
    </>
  );
};

export default ClientVoiceSection; 